/**
 * IP Intel Report — the in-platform order modal for ONE known technology
 * (the tech card's "Get IP Intel Report").
 *
 * Screens: offer (seven modules + price, "Get the report", sample reports)
 * → [details, only when the signed-in profile lacks a required field]
 * → Razorpay → Payment received | Payment failed.
 *
 * Same order of operations as the public page's PaymentSheet: the server
 * authors the order (price re-resolved for this buyer; the tech id rides in
 * `source` and is validated there), Razorpay collects, and the server
 * verifies the signature before anything is promised. For a tech with a
 * displayed TRL the server files the intake itself (verify answers
 * auto_intake: true) — otherwise the buyer gets the usual intake link.
 *
 * Reuses the public page's API client, copy (QUESTIONS), modal shell and
 * stylesheet, so the two cannot drift apart. Events are the public page's
 * ipintel_* names with {surface: 'platform', entry, tech_id}.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { apiService } from '../../services/apiService';
import { loadRazorpay } from '../../utils/razorpayCheckout';
import { IprModal, ModalCloseVia } from '../../landing-page/components/ipintel/Modal';
import { IpIntelStyles } from '../../landing-page/components/ipintel/styles';
import {
  API_BASE, CouponQuote, ipIntelApi, IpIntelConfig, IpIntelSample, LeadDownload, OrderResponse, VerifyResponse,
  leadPageUrl, sampleDeliveryOf, storedSource, visitorIds,
} from '../../landing-page/components/ipintel/api';
import { CONTACT_EMAIL, EMAIL_RE, FALLBACK_SAMPLES, GSTIN_RE, QUESTIONS } from '../../landing-page/components/ipintel/content';
import { usdAlt } from '../../landing-page/components/ipintel/usd';
import { tcTrack } from './track';
import { useTechCardStyles } from './styles';

interface Props {
  techId: string;
  techTitle: string;
  /** Where the modal was opened from (tracking + the order's source):
   *  the platform's tech card or IP Intel tab, or a public tech page. */
  entry?: 'techcard' | 'tab' | 'techpage';
  onClose: () => void;
}

type Screen = 'offer' | 'details' | 'lead' | 'paid' | 'failed';
type PayPhase = 'idle' | 'creating' | 'checkout' | 'verifying';

interface Buyer { name: string; email: string; phone: string; organisation: string; designation: string; gstin: string }
const EMPTY_BUYER: Buyer = { name: '', email: '', phone: '', organisation: '', designation: '', gstin: '' };

interface Lead { first_name: string; last_name: string; email: string; organisation: string; designation: string }
const EMPTY_LEAD: Lead = { first_name: '', last_name: '', email: '', organisation: '', designation: '' };

/** verify's answer, plus the known-tech fields the server adds. */
type Verified = VerifyResponse & { auto_intake?: boolean; email?: string | null; status?: string };

/** The public sheet's key: a payment Razorpay reported but we could not
 *  confirm blocks paying again in this tab (on either surface). */
const UNCONFIRMED_KEY = 'ipintel_unconfirmed_ref';
const readUnconfirmed = (): string => { try { return sessionStorage.getItem(UNCONFIRMED_KEY) || ''; } catch { return ''; } };

const clip = (v: unknown, n = 200): string | null =>
  v === null || v === undefined || v === '' ? null : String(v).slice(0, n);

const buyerErrors = (b: Buyer, needGstin: boolean): Partial<Record<keyof Buyer, string>> => {
  const e: Partial<Record<keyof Buyer, string>> = {};
  if (b.name.trim().length < 2) e.name = 'Please enter your full name.';
  if (!EMAIL_RE.test(b.email.trim())) e.email = 'Please enter a valid e-mail address.';
  const digits = b.phone.replace(/\D/g, '');
  if (!/^[+\d][\d\s\-()]*$/.test(b.phone.trim()) || digits.length < 7 || digits.length > 15) e.phone = 'Please enter a valid phone number.';
  if (!b.organisation.trim()) e.organisation = 'Please enter your organisation.';
  if (!b.designation.trim()) e.designation = 'Please enter your designation.';
  if (needGstin && b.gstin.trim() && !GSTIN_RE.test(b.gstin.trim().toUpperCase())) e.gstin = 'A GSTIN is 15 characters, e.g. 27AAPFU0939F1ZV.';
  return e;
};

const leadValid = (l: Lead) =>
  !!(l.first_name.trim() && l.last_name.trim() && EMAIL_RE.test(l.email.trim()) && l.organisation.trim() && l.designation.trim());

const absolute = (url: string) => (/^https?:\/\//i.test(url) ? url : `${API_BASE()}${url}`);

/** Fetch-as-blob keeps our filename across origins; a plain link is the fallback. */
async function saveFile(d: LeadDownload): Promise<void> {
  const href = absolute(d.url);
  try {
    const res = await fetch(href, { mode: 'cors', credentials: 'omit' });
    if (!res.ok) throw new Error(String(res.status));
    const obj = URL.createObjectURL(await res.blob());
    const a = document.createElement('a');
    a.href = obj; a.download = d.filename || 'UniversityTech-Sample-Report.pdf'; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(obj), 30000);
  } catch {
    const a = document.createElement('a');
    a.href = href; a.download = d.filename || ''; a.rel = 'noopener';
    document.body.appendChild(a); a.click(); a.remove();
  }
}

const CheckIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M5 12.5l4.2 4.2L19 7" /></svg>
);
const WarnIcon: React.FC = () => (
  <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 3.5l9.5 16.5h-19z" /><path d="M12 10v4.5" /><path d="M12 17.6v.1" />
  </svg>
);

export const IpIntelOrderModal: React.FC<Props> = ({ techId, techTitle, entry = 'techcard', onClose }) => {
  useTechCardStyles();
  const surface = entry === 'techpage' ? 'techpage' : 'platform';
  const base = useMemo(() => ({ surface, entry, tech_id: techId }), [surface, entry, techId]);
  const track = (action: string, props?: Record<string, unknown>) => tcTrack(action, { ...base, ...(props || {}) });

  const [config, setConfig] = useState<IpIntelConfig | null>(null);
  const [configState, setConfigState] = useState<'loading' | 'ready' | 'error'>('loading');
  const [screen, setScreen] = useState<Screen>('offer');
  const [phase, setPhase] = useState<PayPhase>('idle');
  const phaseRef = useRef<PayPhase>('idle');
  phaseRef.current = phase;
  const [message, setMessage] = useState<{ kind: 'error' | 'info' | 'success'; text: string } | null>(null);
  const [buyer, setBuyer] = useState<Buyer>(EMPTY_BUYER);
  const [buyerErr, setBuyerErr] = useState<Partial<Record<keyof Buyer, string>>>({});
  const [unconfirmedRef, setUnconfirmedRef] = useState<string>(readUnconfirmed);
  const [paid, setPaid] = useState<{ email: string; autoIntake: boolean; intakeUrl: string; orderRef: string } | null>(null);
  const [failure, setFailure] = useState<{ kind: 'declined' | 'unconfirmed'; text: string; orderRef: string } | null>(null);
  const lastOrder = useRef<{ key: string; order: OrderResponse } | null>(null);
  const declinedRef = useRef<string>('');
  const busyRef = useRef(false);

  // Samples
  const [samplesOpen, setSamplesOpen] = useState(false);
  const [picked, setPicked] = useState<Record<string, boolean>>({});
  const [lead, setLead] = useState<Lead>(EMPTY_LEAD);
  const [leadState, setLeadState] = useState<'idle' | 'submitting' | 'downloading' | 'done' | 'emailed'>('idle');
  const [leadError, setLeadError] = useState('');
  const [links, setLinks] = useState<LeadDownload[]>([]);
  /** Where the sample mail went (e-mail / both modes) and how many reports it carried. */
  const [sentTo, setSentTo] = useState<{ email: string; count: number } | null>(null);
  const [hp, setHp] = useState('');

  const signedInId = apiService.getStoredUserId?.() || '';

  const loadConfig = () => {
    setConfigState('loading');
    ipIntelApi.getConfig().then(res => {
      if (res.ok && res.data) { setConfig(res.data); setConfigState('ready'); }
      else setConfigState('error');
      track('ipintel_page_view', {
        available: res.ok ? !!res.data?.available : null,
        region: res.ok ? res.data?.region?.code || null : null,
      });
    });
  };

  useEffect(() => {
    loadConfig();
    // Prefill buyer + sample-lead details from the signed-in profile.
    if (!signedInId) return;
    let live = true;
    apiService.getProfile(signedInId).then(res => {
      if (!live || !res.success || !res.data) return;
      const u: any = (res.data as any).user || {};
      const p: any = (res.data as any).profile || {};
      const name = [u.first_name, u.last_name].filter(Boolean).join(' ').trim();
      setBuyer(prev => ({
        ...prev,
        name: prev.name || name,
        email: prev.email || u.email || '',
        phone: prev.phone || u.phone || '',
        organisation: prev.organisation || p.organisation_name || '',
        designation: prev.designation || p.designation || '',
      }));
      setLead(prev => ({
        first_name: prev.first_name || u.first_name || '',
        last_name: prev.last_name || u.last_name || '',
        email: prev.email || u.email || '',
        organisation: prev.organisation || p.organisation_name || '',
        designation: prev.designation || p.designation || '',
      }));
    }).catch(() => { /* prefill is a convenience */ });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const busy = phase !== 'idle';
  // ---- coupon code (owner, 2026-09-29) ----
  const [coupon, setCoupon] = useState<CouponQuote | null>(null);
  // The strip's field is always visible now; "opened" is tracked on the first
  // focus, so ipintel_coupon_open keeps meaning "started typing a code".
  const couponOpened = useRef(false);
  const [couponInput, setCouponInput] = useState('');
  const [couponBusy, setCouponBusy] = useState(false);
  const [couponError, setCouponError] = useState('');
  const applyCoupon = async () => {
    const code = couponInput.trim().toUpperCase();
    if (!code || couponBusy) return;
    setCouponBusy(true); setCouponError('');
    const res = await ipIntelApi.previewCoupon(code);
    setCouponBusy(false);
    track('ipintel_coupon_apply', { position: entry === 'techpage' ? 'techpage' : 'techcard', ok: res.ok });
    if (!res.ok) { setCoupon(null); setCouponError(res.error || 'That code is not valid.'); return; }
    setCoupon(res.data);
    lastOrder.current = null;   // the price changed: never reuse an order made without it
  };
  const removeCoupon = () => { setCoupon(null); setCouponInput(''); setCouponError(''); lastOrder.current = null; };
  const priced = configState === 'ready' && !!config?.available && !!config.display;
  const gatewayOff = priced && config!.payments_enabled === false;
  const ready = priced && !gatewayOff;
  const needGstin = (config?.region?.code || '').toUpperCase() === 'IN' || (config?.currency || '').toUpperCase() === 'INR';
  const turnaround = config?.turnaround_days || 7;
  const samples: IpIntelSample[] = configState === 'ready' && config?.samples?.length ? config.samples
    : configState === 'loading' ? [] : FALLBACK_SAMPLES;
  const chosen = samples.filter(s => picked[s.id]);
  // How samples reach the visitor (Admin → IP Intel Report → Samples): the
  // words before the form; the server's answer decides what happens after.
  const sampleMode = sampleDeliveryOf(config?.sample_delivery);
  const emailOnly = sampleMode === 'email';
  const listLabel = priced
    ? (config!.display!.price_with_tax_label || `${config!.display!.price}${config!.tax_minor ? ` + ${config!.tax_label || 'GST'}` : ''}`)
    : '';
  // A coupon only changes what is SHOWN here; the order re-checks the code.
  const priceLabel = coupon ? (coupon.display.price_with_tax_label || coupon.display.price) : listLabel;
  const totalLabel = coupon ? coupon.display.total : (priced ? config!.display!.total : '');

  const close = (via: ModalCloseVia | 'done' | 'cancel') => {
    if (busyRef.current) return;
    tcTrack('techcard_ipintel_close', { tech_id: techId, surface, entry, screen, via });
    onClose();
  };

  // ---------------------------------------------------------------- payment

  const verify = async (resp: any, order: OrderResponse, details: Buyer) => {
    setPhase('verifying');
    setMessage({ kind: 'info', text: 'Payment received — confirming it with the bank…' });
    const v = await ipIntelApi.verifyOrder({
      razorpay_order_id: resp?.razorpay_order_id || order.razorpay_order_id,
      razorpay_payment_id: resp?.razorpay_payment_id,
      razorpay_signature: resp?.razorpay_signature,
    });
    busyRef.current = false;
    setPhase('idle');
    setMessage(null);
    const d = (v.ok ? v.data : null) as Verified | null;
    if (d && (d.order_ref || d.intake_url || d.auto_intake)) {
      track('ipintel_payment_verified', { order_ref: d.order_ref || order.order_ref, auto_intake: !!d.auto_intake });
      lastOrder.current = null;
      setPaid({
        email: (d.email || details.email || '').trim(),
        autoIntake: !!d.auto_intake,
        intakeUrl: d.auto_intake ? '' : (d.intake_url || ''),
        orderRef: d.order_ref || order.order_ref,
      });
      setScreen('paid');
      return;
    }
    // The money may well have moved; the webhook still settles the order.
    track('ipintel_checkout_failed', { order_ref: order.order_ref, code: 'verify_failed', description: clip(v.ok ? 'no order_ref' : v.error) });
    try { sessionStorage.setItem(UNCONFIRMED_KEY, order.order_ref); } catch { /* ignore */ }
    setUnconfirmedRef(order.order_ref);
    const raw = !v.ok && v.status !== 0 ? (v.error || '').trim() : '';
    setFailure({
      kind: 'unconfirmed',
      orderRef: order.order_ref,
      text: `${raw ? `${raw}${/[.!?]$/.test(raw) ? '' : '.'} ` : ''}We could not confirm the payment here (order ${order.order_ref}). `
        + `If you were charged, you will receive an e-mail shortly — or write to ${CONTACT_EMAIL} quoting that reference.`,
    });
    setScreen('failed');
  };

  const openCheckout = async (order: OrderResponse, details: Buyer) => {
    const ok = await loadRazorpay();
    if (!ok || !(window as any).Razorpay) {
      track('ipintel_checkout_failed', { order_ref: order.order_ref, code: 'script_load', description: 'checkout.js failed to load' });
      busyRef.current = false;
      setPhase('idle');
      setMessage({ kind: 'error', text: 'Could not load the payment window — check your connection and try again.' });
      return;
    }
    declinedRef.current = '';
    const rz = new (window as any).Razorpay({
      key: order.key_id || config?.razorpay_key_id,
      order_id: order.razorpay_order_id,
      amount: order.amount,
      currency: order.currency,
      name: 'UniversityTech.io',
      description: `IP Intelligence Report · ${order.order_ref}`,
      prefill: {
        name: order.prefill?.name || details.name,
        email: order.prefill?.email || details.email,
        contact: order.prefill?.contact || details.phone,
      },
      notes: { product: 'ip_intel', order_ref: order.order_ref, tech_id: techId },
      theme: { color: '#886A44' },
      handler: (resp: any) => { void verify(resp, order, details); },
      modal: {
        ondismiss: () => {
          if (phaseRef.current !== 'checkout') return;
          busyRef.current = false;
          setPhase('idle');
          if (declinedRef.current) {
            // Razorpay kept its sheet open for a retry and the buyer gave up.
            track('ipintel_checkout_dismissed', { order_ref: order.order_ref, after_failure: true });
            setFailure({ kind: 'declined', orderRef: order.order_ref, text: 'Nothing was charged. Retry the payment to continue with the report.' });
            setScreen('failed');
            return;
          }
          track('ipintel_checkout_dismissed', { order_ref: order.order_ref });
          setMessage({ kind: 'info', text: 'Payment window closed — you have not been charged. You can try again whenever you are ready.' });
        },
      },
    });
    rz.on('payment.failed', (r: any) => {
      const e = r?.error || {};
      declinedRef.current = String(e.description || 'declined');
      track('ipintel_checkout_failed', {
        order_ref: order.order_ref, code: clip(e.code, 60), description: clip(e.description),
        reason: clip(e.reason, 80), step: clip(e.step, 60),
      });
    });
    setPhase('checkout');
    rz.open();
    track('ipintel_checkout_opened', { order_ref: order.order_ref, amount: order.amount, currency: order.currency });
  };

  const pay = async (details: Buyer) => {
    if (busyRef.current || !ready || unconfirmedRef) return;
    busyRef.current = true;
    setMessage(null);
    const clean: Buyer = {
      name: details.name.trim(), email: details.email.trim().toLowerCase(), phone: details.phone.trim(),
      organisation: details.organisation.trim(), designation: details.designation.trim(),
      gstin: needGstin ? details.gstin.trim().toUpperCase() : '',
    };
    track('ipintel_payment_details_submit', { position: 'techcard', has_gstin: !!clean.gstin, signed_in: !!signedInId });
    const key = JSON.stringify({ ...clean, coupon: coupon?.code || '' });
    let order = lastOrder.current?.key === key ? lastOrder.current.order : null;
    if (!order) {
      setPhase('creating');
      const res = await ipIntelApi.createOrder({
        ...clean,
        gstin: clean.gstin || undefined,
        user_id: signedInId || undefined,
        source: {
          ...storedSource(), ...visitorIds(),
          position: 'techcard', path: window.location.pathname,
          tech_id: techId, surface, entry,
        },
        website: hp,
        coupon: coupon?.code || undefined,
      });
      if (!res.ok || !res.data?.razorpay_order_id) {
        track('ipintel_checkout_failed', { code: 'order_create', description: clip(res.ok ? 'no order id' : res.error), status: res.status, position: 'techcard' });
        busyRef.current = false;
        setPhase('idle');
        setMessage({ kind: 'error', text: res.ok ? 'Could not start the payment. Please try again.' : res.error });
        // The code stopped applying between the preview and the order (expired,
        // used up): drop it so the next attempt shows the real price.
        if (!res.ok && coupon && /code/i.test(res.error || '')) { setCoupon(null); setCouponError(res.error); }
        return;
      }
      order = res.data;
      lastOrder.current = { key, order };
      track('ipintel_order_created', { order_ref: order.order_ref, position: 'techcard', reused: false });
    } else {
      track('ipintel_order_created', { order_ref: order.order_ref, position: 'techcard', reused: true });
    }
    setScreen('offer');
    await openCheckout(order, clean);
  };

  const onGetReport = () => {
    if (busyRef.current) return;
    track('ipintel_click_order', { position: 'techcard' });
    if (!ready || unconfirmedRef) return;
    const errs = buyerErrors(buyer, needGstin);
    if (Object.keys(errs).length) {
      // Something the order needs is missing from the profile: ask for it.
      setBuyerErr({});
      track('ipintel_payment_modal_open', { position: 'techcard', missing: Object.keys(errs).join(',') });
      setScreen('details');
      return;
    }
    void pay(buyer);
  };

  const submitDetails = () => {
    const errs = buyerErrors(buyer, needGstin);
    setBuyerErr(errs);
    if (Object.keys(errs).length) {
      track('ipintel_payment_details_invalid', { position: 'techcard', fields: Object.keys(errs).join(',') });
      document.getElementById(`tcx-ipi-${Object.keys(errs)[0]}`)?.focus();
      return;
    }
    void pay(buyer);
  };

  const retry = () => {
    const last = lastOrder.current;
    track('ipintel_payment_retry', { order_ref: last?.order.order_ref || null });
    setFailure(null);
    setScreen('offer');
    if (last && !busyRef.current) {
      busyRef.current = true;
      void openCheckout(last.order, JSON.parse(last.key) as Buyer);
    } else {
      onGetReport();
    }
  };

  // ---------------------------------------------------------------- samples

  const toggleSamples = () => {
    const next = !samplesOpen;
    if (next) track('ipintel_click_see_samples', { position: 'techcard' });
    setSamplesOpen(next);
  };

  const togglePick = (s: IpIntelSample, i: number) => {
    const checked = !picked[s.id];
    track('ipintel_sample_toggle', { report: s.id, checked, via: 'checkbox', index: i + 1 });
    setPicked(prev => ({ ...prev, [s.id]: checked }));
    if (leadState === 'done' || leadState === 'emailed') setLeadState('idle');
  };

  const onDownloadClick = () => {
    if (!chosen.length) return;
    track('ipintel_click_download', { count: chosen.length, reports: chosen.map(s => s.id).join(','), mode: sampleMode });
    track('ipintel_lead_form_open', { count: chosen.length });
    setLeadError('');
    setScreen('lead');
  };

  const submitLead = async () => {
    if (!leadValid(lead) || leadState === 'submitting' || leadState === 'downloading') return;
    setLeadState('submitting');
    setLeadError('');
    setSentTo(null);
    track('ipintel_lead_submit', { count: chosen.length, reused: false, mode: sampleMode });
    const res = await ipIntelApi.submitLead({
      first_name: lead.first_name.trim(), last_name: lead.last_name.trim(), email: lead.email.trim().toLowerCase(),
      organisation: lead.organisation.trim(), designation: lead.designation.trim(),
      reports: chosen.map(s => s.id),
      user_id: signedInId || undefined,
      website: hp,
      surface: entry,
      page_url: leadPageUrl() || undefined,
      ...visitorIds(),
    });
    const data = res.ok ? res.data : null;
    const reports = chosen.map(s => s.id).join(',');
    // E-mail only: the mail went and, by design, no download link came back.
    if (data && !data.downloads?.length && data.emailed) {
      setSentTo({ email: data.email || lead.email.trim().toLowerCase(), count: chosen.length });
      track('ipintel_lead_emailed', { count: chosen.length, reused: false, reports, mode: data.mode || 'email' });
      setLeadState('emailed');
      setScreen('offer');
      setSamplesOpen(true);
      return;
    }
    if (!data || !data.downloads?.length) {
      const msg = !res.ok ? res.error
        : data?.error || (data?.mode === 'email'
          ? "We couldn't e-mail the reports just now. Please try again in a few minutes."
          : 'The reports could not be prepared. Please try again.');
      track('ipintel_lead_error', { error: clip(msg), count: chosen.length, mode: data?.mode || sampleMode });
      setLeadState('idle');
      setLeadError(msg);
      return;
    }
    const dls = data.downloads.map(d => ({ ...d, filename: d.filename || samples.find(s => s.id === d.id)?.filename || d.filename }));
    track('ipintel_lead_success', { count: dls.length, reused: false, reports: dls.map(d => d.id).join(',') });
    if (data.emailed) {
      setSentTo({ email: data.email || lead.email.trim().toLowerCase(), count: chosen.length });
      track('ipintel_lead_emailed', { count: chosen.length, reused: false, reports, mode: data.mode || 'both' });
    }
    setLinks(dls);
    setLeadState('downloading');
    setScreen('offer');
    setSamplesOpen(true);
    await Promise.all(dls.map((d, i) => new Promise<void>(resolve => {
      setTimeout(() => { void saveFile(d).finally(resolve); }, i * 300);
    })));
    setLeadState('done');
  };

  // ---------------------------------------------------------------- render

  const buyerField = (k: keyof Buyer, label: string, props: React.InputHTMLAttributes<HTMLInputElement>, optional?: boolean) => (
    <div>
      <label className="ipr-label" htmlFor={`tcx-ipi-${k}`}>{label}{optional && <span className="ipr-opt"> (optional)</span>}</label>
      <input
        id={`tcx-ipi-${k}`}
        className={`ipr-input${buyerErr[k] ? ' is-invalid' : ''}`}
        value={buyer[k]}
        onChange={e => {
          const v = k === 'gstin' ? e.target.value.toUpperCase() : e.target.value;
          setBuyer(prev => ({ ...prev, [k]: v }));
          if (buyerErr[k]) setBuyerErr(prev => ({ ...prev, [k]: undefined }));
        }}
        disabled={busy}
        aria-invalid={!!buyerErr[k]}
        {...props}
      />
      {buyerErr[k] && <div className="ipr-field-error">{buyerErr[k]}</div>}
    </div>
  );

  const leadField = (k: keyof Lead, label: string, placeholder: string, type = 'text', auto?: string) => (
    <div>
      <label className="ipr-label" htmlFor={`tcx-ipi-lead-${k}`}>{label}</label>
      <input id={`tcx-ipi-lead-${k}`} type={type} className="ipr-input" placeholder={placeholder} value={lead[k]}
        onChange={e => setLead(prev => ({ ...prev, [k]: e.target.value }))} autoComplete={auto}
        disabled={leadState === 'submitting'} />
    </div>
  );

  const honeypot = (
    <div className="ipr-hp" aria-hidden="true">
      <label htmlFor="tcx-ipi-website">Website</label>
      <input id="tcx-ipi-website" tabIndex={-1} autoComplete="off" value={hp} onChange={e => setHp(e.target.value)} />
    </div>
  );

  const payLabel = phase === 'creating' ? 'Creating your order…'
    : phase === 'checkout' ? 'Waiting for payment…'
    : phase === 'verifying' ? 'Confirming payment…'
    : 'Get the report';

  let body: React.ReactNode;
  let width = 880;

  if (screen === 'paid' && paid) {
    width = 460;
    body = (
      <div className="tcx-ipi-status" role="status">
        <div className="tcx-ipi-icon is-ok"><CheckIcon /></div>
        <h3 id="tcx-ipi-title">Payment received</h3>
        {paid.autoIntake || !paid.intakeUrl ? (
          <p>Your IP Intel Report will be sent to your registered email, <strong>{paid.email || 'on file'}</strong>, within {turnaround} days.</p>
        ) : (
          <p>
            One more step: tell us the technology&apos;s readiness level and stage of development, and your {turnaround}-day clock starts.
            We have also e-mailed the link to <strong>{paid.email || 'your registered email'}</strong>.
          </p>
        )}
        <div className="tcx-ipi-actions" style={{ justifyContent: 'center' }}>
          {!paid.autoIntake && paid.intakeUrl && (
            <a className="ipr-btn-primary" href={paid.intakeUrl} target="_blank" rel="noopener noreferrer"
              onClick={() => track('ipintel_intake_open', { order_ref: paid.orderRef })}>Complete the intake</a>
          )}
          <button type="button" className={!paid.autoIntake && paid.intakeUrl ? 'tcx-ipi-beige' : 'ipr-btn-primary'} onClick={() => close('done')}>Done</button>
        </div>
        <div style={{ fontSize: 12, color: 'var(--ipr-gray-300)', marginTop: 14 }}>Order {paid.orderRef}</div>
      </div>
    );
  } else if (screen === 'failed' && failure) {
    width = 460;
    body = (
      <div className="tcx-ipi-status" role="alert">
        <div className="tcx-ipi-icon is-bad"><WarnIcon /></div>
        <h3 id="tcx-ipi-title">{failure.kind === 'declined' ? 'Payment failed' : 'Payment not confirmed'}</h3>
        <p>{failure.text}</p>
        <div className="tcx-ipi-actions" style={{ justifyContent: 'center' }}>
          {failure.kind === 'declined' ? (
            <>
              <button type="button" className="ipr-btn-primary" onClick={retry}>Retry payment</button>
              <button type="button" className="tcx-ipi-beige" onClick={() => close('cancel')}>Close</button>
            </>
          ) : (
            <button type="button" className="ipr-btn-primary" onClick={() => close('done')}>Done</button>
          )}
        </div>
      </div>
    );
  } else if (screen === 'details') {
    width = 520;
    body = (
      <>
        <h2 id="tcx-ipi-title" className="tcx-ipi-title" style={{ fontSize: 22 }}>Your details</h2>
        <p className="tcx-ipi-sub">For the order and the invoice of your IP Intelligence Report on <strong>{techTitle}</strong>.</p>
        <form noValidate onSubmit={e => { e.preventDefault(); submitDetails(); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          {buyerField('name', 'Full name', { autoComplete: 'name', placeholder: 'Ada Lovelace' })}
          <div className="ipr-grid-2">
            {buyerField('email', 'Work email', { type: 'email', autoComplete: 'email', placeholder: 'you@company.com' })}
            {buyerField('phone', 'Phone', { type: 'tel', autoComplete: 'tel', placeholder: '+91 98765 43210' })}
          </div>
          <div className="ipr-grid-2">
            {buyerField('organisation', 'Organisation', { autoComplete: 'organization', placeholder: 'Company or institute' })}
            {buyerField('designation', 'Designation', { autoComplete: 'organization-title', placeholder: 'e.g. Head of Tech Transfer' })}
          </div>
          {needGstin && buyerField('gstin', 'GSTIN', { placeholder: 'For a GST invoice', maxLength: 15, autoComplete: 'off' }, true)}
          {honeypot}
          {message && <div className={`ipr-alert ${message.kind === 'error' ? 'ipr-alert-error' : 'ipr-alert-info'}`} role="alert">{message.text}</div>}
          <div className="tcx-ipi-actions" style={{ marginTop: 6 }}>
            <button type="submit" className="ipr-btn-primary" disabled={!ready || busy}>
              {busy && <span className="ipr-spin" aria-hidden="true" />}
              {busy ? payLabel : `Continue to payment${totalLabel ? ` · ${totalLabel}` : ''}`}
            </button>
            <button type="button" className="tcx-ipi-beige" disabled={busy} onClick={() => { setMessage(null); setScreen('offer'); }}>Back</button>
          </div>
        </form>
      </>
    );
  } else if (screen === 'lead') {
    width = 520;
    const n = chosen.length;
    body = (
      <>
        <h2 id="tcx-ipi-title" className="tcx-ipi-title" style={{ fontSize: 22 }}>Your details</h2>
        <p className="tcx-ipi-sub">
          {emailOnly
            ? <>We&apos;ll e-mail the {n === 1 ? 'selected sample report' : `${n} selected sample reports`} to you.</>
            : sampleMode === 'both'
              ? <>We&apos;ll download the {n === 1 ? 'selected sample report' : `${n} selected sample reports`} straight away and e-mail you a copy.</>
              : <>We&apos;ll download the {n === 1 ? 'selected sample report' : `${n} selected sample reports`} straight away.</>}
        </p>
        <form noValidate onSubmit={e => { e.preventDefault(); void submitLead(); }} style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div className="ipr-grid-2">
            {leadField('first_name', 'First name', 'Ada', 'text', 'given-name')}
            {leadField('last_name', 'Last name', 'Lovelace', 'text', 'family-name')}
          </div>
          {leadField('email', 'Work email', 'you@company.com', 'email', 'email')}
          {leadField('designation', 'Designation', 'e.g. Head of Tech Transfer', 'text', 'organization-title')}
          {leadField('organisation', 'Organization', 'Your company or institute', 'text', 'organization')}
          {honeypot}
          {leadError && <div className="ipr-alert ipr-alert-error" role="alert">{leadError}</div>}
          <div className="tcx-ipi-actions" style={{ marginTop: 6 }}>
            <button type="submit" className="ipr-btn-primary" disabled={!leadValid(lead) || leadState === 'submitting'}>
              {leadState === 'submitting' && <span className="ipr-spin" aria-hidden="true" />}
              {leadState === 'submitting' ? (emailOnly ? 'Sending…' : 'Preparing…') : emailOnly ? 'Email me the reports' : 'Download'}
            </button>
            <button type="button" className="tcx-ipi-beige" disabled={leadState === 'submitting'}
              onClick={() => { track('ipintel_lead_form_close', { via: 'back', count: n }); setScreen('offer'); }}>Back</button>
          </div>
        </form>
        <div style={{ fontSize: 12, color: 'var(--ipr-gray-300)', marginTop: 12 }}>Used only to send you the reports and relevant follow-ups.</div>
      </>
    );
  } else {
    body = (
      <>
        <h2 id="tcx-ipi-title" className="tcx-ipi-title">IP Intel Report</h2>
        <p className="tcx-ipi-sub">Seven modules, one technology, one document — <strong>{techTitle}</strong>.</p>
        <div className="tcx-ipi-grid">
          {QUESTIONS.map(q => (
            <div key={q.n} className="tcx-ipi-cell">
              <div className="tcx-ipi-n">{q.n}</div>
              <div className="tcx-ipi-mod">{q.module}</div>
              <div className="tcx-ipi-q">{q.q}</div>
            </div>
          ))}
          <div className="tcx-ipi-price" aria-live="polite">
            {configState === 'loading' ? (
              <><span className="ipr-skel" style={{ width: 120, height: 20 }} /><span className="ipr-skel" style={{ width: '90%', height: 12 }} /></>
            ) : priced ? (
              <>
                {coupon && (
                  <div className="tcx-ipi-when" style={{ textDecoration: 'line-through', marginBottom: 2 }}>{listLabel}</div>
                )}
                <div className="tcx-ipi-amount">{priceLabel}</div>
                {/* The USD figure, display only — checkout stays in INR. */}
                <div className="tcx-ipi-when" style={{ fontWeight: 600 }}>{usdAlt(config)}</div>
                <div className="tcx-ipi-when">Delivered to your registered email in {turnaround} days</div>
              </>
            ) : configState === 'error' ? (
              <div className="tcx-ipi-when">
                We couldn&apos;t load the price just now.{' '}
                <button type="button" className="tcx-ipi-linkbtn" onClick={() => { track('ipintel_config_retry', { position: 'techcard' }); loadConfig(); }}>Try again</button>
              </div>
            ) : (
              <div className="tcx-ipi-when">
                Not sold online in your region yet — write to{' '}
                <a href={`mailto:${config?.contact_email || CONTACT_EMAIL}?subject=${encodeURIComponent(`IP Intelligence Report — ${techTitle}`)}`}
                  onClick={() => track('ipintel_click_contact', { position: 'techcard' })}
                  style={{ fontWeight: 700, textDecoration: 'underline' }}>{config?.contact_email || CONTACT_EMAIL}</a>{' '}for a quote.
              </div>
            )}
          </div>
        </div>

        {gatewayOff && (
          <div className="ipr-alert ipr-alert-info" style={{ marginBottom: 14 }}>
            Online payment is switched off for the moment. Write to{' '}
            <a href={`mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(`IP Intelligence Report — ${techTitle}`)}`}
              onClick={() => track('ipintel_click_contact', { position: 'techcard' })}
              style={{ color: 'inherit', fontWeight: 700, textDecoration: 'underline' }}>{CONTACT_EMAIL}</a>{' '}and we will send you a payment link.
          </div>
        )}
        {unconfirmedRef && (
          <div className="ipr-alert ipr-alert-error" role="alert" style={{ marginBottom: 14 }}>
            We could not confirm an earlier payment (order {unconfirmedRef}). If you were charged, you will receive an e-mail shortly — or write to {CONTACT_EMAIL} quoting that reference.{' '}
            <button type="button" className="tcx-ipi-linkbtn" style={{ color: 'inherit' }} onClick={() => {
              track('ipintel_payment_new_order', { position: 'techcard' });
              try { sessionStorage.removeItem(UNCONFIRMED_KEY); } catch { /* ignore */ }
              lastOrder.current = null; setUnconfirmedRef('');
            }}>Sure you were not charged? Start a new order</button>
          </div>
        )}
        {message && (
          <div className={`ipr-alert ${message.kind === 'error' ? 'ipr-alert-error' : message.kind === 'success' ? 'ipr-alert-success' : 'ipr-alert-info'}`}
            role={message.kind === 'error' ? 'alert' : 'status'} style={{ marginBottom: 14 }}>{message.text}</div>
        )}

        {/* The coupon, as a full-width strip right above the pay button (owner,
            2026-09-30) rather than a link tucked into the price card. */}
        {ready && (coupon ? (
          <div className="tcx-ipi-coupon-strip is-applied" aria-live="polite">
            <span className="tcx-ipi-coupon-label">Code {coupon.code} applied</span>
            <span className="tcx-ipi-coupon-note">{coupon.discount_display} off · you pay {priceLabel}</span>
            <button type="button" className="tcx-ipi-linkbtn" onClick={removeCoupon} disabled={busy}>Remove</button>
          </div>
        ) : (
          <form className="tcx-ipi-coupon-strip" onSubmit={e => { e.preventDefault(); void applyCoupon(); }}>
            <label className="tcx-ipi-coupon-label" htmlFor="tcx-ipi-coupon-input">Have a coupon code?</label>
            <input id="tcx-ipi-coupon-input" placeholder="Enter code" value={couponInput} maxLength={32} autoComplete="off"
              onFocus={() => { if (!couponOpened.current) { couponOpened.current = true; track('ipintel_coupon_open', {}); } }}
              onChange={e => { setCouponInput(e.target.value.toUpperCase()); setCouponError(''); }}
              disabled={busy} aria-invalid={!!couponError || undefined}
              aria-describedby={couponError ? 'tcx-ipi-coupon-error' : undefined} />
            <button type="submit" className="tcx-ipi-beige" disabled={busy || couponBusy || !couponInput.trim()}>
              {couponBusy ? 'Checking…' : 'Apply'}
            </button>
          </form>
        ))}
        {ready && !coupon && couponError && (
          <div id="tcx-ipi-coupon-error" role="alert" className="tcx-ipi-coupon-error">{couponError}</div>
        )}

        <div className="tcx-ipi-actions">
          <button type="button" className="ipr-btn-primary" onClick={onGetReport} disabled={!ready || busy || !!unconfirmedRef}>
            {busy && <span className="ipr-spin" aria-hidden="true" />}
            {payLabel}
          </button>
          <button type="button" className="tcx-ipi-beige" onClick={toggleSamples} aria-expanded={samplesOpen} aria-controls="tcx-ipi-samples" disabled={busy}>
            {samplesOpen ? 'Hide sample reports' : emailOnly ? 'Get Sample Reports' : 'Download Sample Report'}
          </button>
          {ready && !busy && !unconfirmedRef && (
            <button type="button" className="tcx-ipi-linkbtn" onClick={() => { setBuyerErr({}); track('ipintel_payment_modal_open', { position: 'techcard', via: 'edit' }); setScreen('details'); }}>
              {needGstin ? 'Edit billing details / add GSTIN' : 'Edit billing details'}
            </button>
          )}
        </div>
        {phase === 'checkout' && (
          <div style={{ fontSize: 12, color: 'var(--ipr-gray-300)', marginTop: 10 }}>
            Payment window gone?{' '}
            <button type="button" className="tcx-ipi-linkbtn" style={{ fontSize: 12 }} onClick={() => {
              track('ipintel_checkout_dismissed', { order_ref: lastOrder.current?.order.order_ref, via: 'reset' });
              busyRef.current = false; setPhase('idle'); setMessage(null);
            }}>Start again</button>
          </div>
        )}

        {samplesOpen && (
          <div className="tcx-ipi-samples" id="tcx-ipi-samples">
            <p className="tcx-ipi-eyebrow">Pick one or more sample reports</p>
            {configState === 'loading' ? (
              <span className="ipr-skel" style={{ width: '100%', height: 44 }} />
            ) : samples.map((s, i) => (
              <label key={s.id} className={`tcx-ipi-check${picked[s.id] ? ' is-checked' : ''}`}>
                <input type="checkbox" checked={!!picked[s.id]} onChange={() => togglePick(s, i)} />
                <span>
                  <span className="tcx-ipi-check-title">{s.title}</span>
                  {s.org && <span className="tcx-ipi-check-org" style={{ display: 'block' }}>{s.org}</span>}
                </span>
              </label>
            ))}
            <div className="tcx-ipi-actions" style={{ marginTop: 14 }}>
              <button type="button" className="ipr-btn-primary" onClick={onDownloadClick}
                disabled={!chosen.length || leadState === 'submitting' || leadState === 'downloading'}>
                {leadState === 'downloading' && <span className="ipr-spin" aria-hidden="true" />}
                {leadState === 'downloading' ? 'Downloading…'
                  : `${emailOnly ? 'Email me' : 'Download'} ${chosen.length || ''}${chosen.length ? ' ' : ''}report${chosen.length === 1 ? '' : 's'}`}
              </button>
            </div>
            {leadState === 'emailed' && sentTo && (
              <div className="ipr-alert ipr-alert-success" role="status" style={{ fontSize: 13, marginTop: 10, lineHeight: 1.6 }}>
                <strong>Sent!</strong> We&apos;ve e-mailed {sentTo.count === 1 ? 'the report' : `${sentTo.count} reports`} to{' '}
                <strong>{sentTo.email}</strong>. If you don&apos;t see {sentTo.count === 1 ? 'it' : 'them'} in a few minutes,
                check your spam folder.{' '}
                <button type="button" className="tcx-ipi-linkbtn" onClick={() => {
                  track('ipintel_lead_send_again', { count: sentTo.count });
                  setLeadError(''); setLeadState('idle'); setScreen('lead');
                }}>Wrong address? Send again</button>
              </div>
            )}
            {leadState === 'done' && links.length > 0 && (
              <div role="status" style={{ fontSize: 13, color: 'var(--ipr-gray-300)', marginTop: 10, lineHeight: 1.6 }}>
                Your download has started. Didn&apos;t get a file?{' '}
                {links.map((l, i) => (
                  <React.Fragment key={l.id}>
                    {i > 0 && ' · '}
                    <a href={absolute(l.url)} download={l.filename} style={{ color: 'var(--ipr-primary-600)', fontWeight: 600, textDecoration: 'underline' }}
                      onClick={() => track('ipintel_click_download', { count: 1, retry: true, report: l.id })}>
                      {l.title || samples.find(s => s.id === l.id)?.title || l.filename || l.id}
                    </a>
                  </React.Fragment>
                ))}
                {sentTo && <><br />We&apos;ve also e-mailed {sentTo.count === 1 ? 'the report' : `${sentTo.count} reports`} to <strong>{sentTo.email}</strong>.</>}
              </div>
            )}
          </div>
        )}
      </>
    );
  }

  if (typeof document === 'undefined') return null;
  return createPortal(
    <div className="ipr tcx-ipr">
      <IpIntelStyles />
      <IprModal labelledBy="tcx-ipi-title" onClose={via => close(via)} locked={busy} maxWidth={width}>
        {body}
      </IprModal>
    </div>,
    document.body,
  );
};

export default IpIntelOrderModal;
